import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { Chart } from "chart.js";
import { TranslateService } from "@ngx-translate/core";
import { Helper } from "app/shared/helper";
import { LegendItem } from "app/shared/data-interaction";

@Component({
  selector: "app-electricity-cons-chart",
  templateUrl: "./electricity-cons-chart.component.html",
  styleUrls: ["./electricity-cons-chart.component.css"]
})
export class ElectricityConsChartComponent implements OnInit {
  @ViewChild("chartElecCons") canvas;
  @Input() type;
  @Input() chart_id;
  @Input() options;
  @Input() unit;
  @Input() indicator;
  @Input() sample_data;
  @Input() show_network_mix;
  @Input() chartData;
  @Input() value_type;
  @Input() yAxeLabel: string;
  @Input() totals_in_tooltip;

  public areaChartLabels: number[];
  private chart: Chart;
  private yAxes_def;
  private xAxes_def;
  private show_gas_debt: boolean;
  private localOptions;
  private conversionFactor;
  private legendItems: LegendItem[] = [];

  constructor(
    private helper: Helper,
    private translateService: TranslateService
  ) {}

  ngOnInit() {}

  ngOnChanges(changes) {
    if (changes.options && changes.options.currentValue) {
      this.localOptions = JSON.parse(JSON.stringify(this.options));
      if (this.type === "bar") {
        this.localOptions.scales.yAxes[0]["ticks"] = {
          fontColor: "black",
          fontSize: 12,
          callback: value => {
            return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
          }
        };
      }
    }
    var unit_from_config = this.helper.getUnitIdFromFilterValue(
      this.value_type,
      this.unit[this.indicator]
    );
    this.updatechartDataForDesiredUnit(unit_from_config);
    this.createElcConsChart(unit_from_config);
    this.updateLegend();
  }

  getTranslatedLabel(labelToTranslate) {
    return this.translateService.instant("DATA." + labelToTranslate);
  }

  createElcConsChart(unit_from_config) {
    this.resetChart();
    var i;
    if (typeof this.chartData !== "undefined") {
      for (i = 0; i < this.chartData["datasets"].length; i++) {
        this.chartData["datasets"][i]["label"] = this.getTranslatedLabel(
          this.chartData["datasets"][i]["label"]
        );
      }
      // DEFINE Y AXIS  LABEL WITH UNIT
      if (this.indicator == "energy") {
        var y_label_type = "Y_LABEL_NAME";
      } else if (this.indicator == "co2") {
        var y_label_type = "Y_LABEL_NAME_CO2";
      }
      this.yAxeLabel = this.translateService.instant(
        `DASHBOARD.CHARTS.${this.chart_id}.${y_label_type}`
      );
      this.yAxeLabel += this.translateService.instant(
        `UNITS.${unit_from_config["translate_unit"]}`
      );
      // Define behavior of tooltip label for the chart
      this.localOptions.tooltips.callbacks.label = (item, data) => {
        let unit = this.translateService.instant(
          `UNITS.${unit_from_config["translate_unit"]}`
        );
        return [
          `${data.datasets[item.datasetIndex]
            .label} : ${this.helper.getThousandSeparatorFromNumber(
            item.yLabel
          )} ` + unit
        ];
      };
      this.localOptions.responsive = true;
      this.localOptions["maintainAspectRatio"] = false;
      this.localOptions.legend.display = false;
      const ctx = this.canvas.nativeElement.getContext("2d");
      this.xAxes_def = [
        {
          stacked: true,
          display: true,
          id: "x_def",
          ticks: {
            fontColor: "black",
            fontSize: 12
          }
        }
      ];
      this.yAxes_def = [
        {
          stacked: true,
          display: true,
          id: "mwh",
          scaleLabel: {
            display: true,
            labelString: this.yAxeLabel,
            fontColor: "black",
            fontSize: 14
          },
          ticks: {
            callback: value => {
              return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");
            },
            fontColor: "black",
            fontSize: 12
          }
        }
      ];
      this.localOptions.scales.yAxes = this.yAxes_def;
      // configure tooltip footer to display total values
      // don't display if not specified in configuration and for % (total will always be 100%)
      if (this.value_type != "pourcent" && this.totals_in_tooltip) {
        this.localOptions.tooltips.callbacks.footer = (item, data) => {
          // hovered data indices
          const dataIndex = item[0].index;
          // raw values
          let total = this.chartData.totals[dataIndex];
          total *= this.conversionFactor;
          let unit = unit_from_config["translate_unit"];
          // format values
          [total, unit] = this.helper.convertTooBigValue(total, unit);
          total = this.helper.getThousandSeparatorFromNumber(total);
          unit = this.translateService.instant(`UNITS.${unit}`);
          const total_translation = this.translateService.instant(
            `DASHBOARD.CHARTS.${this.chart_id}.TOTAL_TOOLTIP`
          );
          return [total_translation, total + " " + unit];
        };
      } else {
        // disable the footer callback to not display the totals
        this.localOptions.tooltips.callbacks.footer = (item, data) => {
          return [];
        };
      }
      this.chart = new Chart(ctx, {
        type: this.type,
        data: this.chartData,
        options: this.localOptions
      });
    }
  }

  resetChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = void 0;
    }
  }

  updatechartDataForDesiredUnit(unit_from_config) {
    if (typeof this.chartData !== "undefined") {
      // Convert unit
      let currentUnit = this.chartData.datasets[0].unit.toLowerCase();
      if (this.value_type != "pourcent") {
        this.conversionFactor =
          unit_from_config.convert_factor_from[currentUnit];
      } else {
        this.conversionFactor = 1;
      }
      this.chartData.datasets.forEach((dataset, datasetIndex: number): void => {
        dataset.data.forEach((dataValue, valueIndex: number): void => {
          let convertedValue = dataValue * this.conversionFactor;
          this.chartData.datasets[datasetIndex].data[
            valueIndex
          ] = convertedValue;
        });
      });
      let unit = this.translateService.instant(
        `UNITS.${unit_from_config["translate_unit"]}`
      );
    }
  }

  updateLegend() {
    this.legendItems = [];
    if (this.chart) {
      // legend element is corresponds to each dataset and its color
      this.chart.data.datasets.forEach(dataset => {
        const legendItem: LegendItem = {
          label: dataset.label,
          color: dataset.backgroundColor,
          borderColor: dataset.backgroundColor,
          borderStyle: "none",
          clicked: false,
          datasetIndices: []
        };

        this.legendItems.push(legendItem);
      });
    }
  }

  onLegendClick(legendIndex: number) {
    let clickedDataset = this.chart.data.datasets[legendIndex];
    clickedDataset.hidden = !clickedDataset.hidden;
    this.chart.update();
  }
}
